/**
 * Triggers a callback when the user clicks outside of the specified element
 * Accepts optional active prop to control when the listener is active
 *
 * @param ref - The ref to the element to listen for outside clicks
 * @param callback - The callback to trigger when the user clicks outside of the element
 * @param active - Whether the listener is active (optional)
 * @returns void
 */

import { useEffect, useCallback } from "react";

interface UseOutsideClickProps {
  ref: React.RefObject<HTMLElement | null> | undefined;
  callback: (event?: MouseEvent) => void;
  active?: boolean;
  excludeRef?: React.RefObject<HTMLElement | null>;
}

export const useOutsideClick = (props: UseOutsideClickProps) => {
  const { ref = undefined, callback, active = true, excludeRef } = props;

  const handleClick = useCallback(
    (event: MouseEvent) => {
      if (!active) return;
      if (event.target instanceof Node && excludeRef?.current?.contains(event.target)) return;
      if (ref?.current && !ref.current.contains(event.target as Node)) {
        callback(event);
      }
    },
    [ref, active, callback, excludeRef],
  );

  useEffect(() => {
    if (typeof window === "undefined") return;

    window.addEventListener("click", handleClick);

    return () => {
      window.removeEventListener("click", handleClick);
    };
  }, [handleClick]);
};
